import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { Mail, Phone, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { contactService } from '../services/contactService';

const Contact = () => {
  const { t } = useTranslation();
  const [activeForm, setActiveForm] = useState('general'); // 'general', 'consultation', 'quote'
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const onSubmit = async (data) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await contactService.submit({ ...data, formType: activeForm });
      setSubmitted(true);
      reset();
      setTimeout(() => setSubmitted(false), 6000);
    } catch (err) {
      setSubmitError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formTypes = [
    { id: 'general', label: t('contact.general'), icon: Mail },
    { id: 'consultation', label: t('contact.consultation'), icon: Phone },
    { id: 'quote', label: t('contact.quote'), icon: Send },
  ];

  return (
    <>
      <Helmet>
        <title>Contact Us - mwbTechs</title>
        <meta
          name="description"
          content="Get in touch with mwbTechs. Request a consultation, get a project quote, or reach out for general inquiries."
        />
      </Helmet>

      <div className="pt-20 min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container-custom py-8 sm:py-16">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 sm:mb-12"
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
              {t('contact.title1')} <span className="gradient-text">{t('contact.title2')}</span>
            </h1>
            <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto px-2">
              {t('contact.subtitle')}
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-6">{t('contact.info')}</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">{t('contact.email')}</div>
                      <a href="mailto:info@mwbtechs.com" className="text-gray-600 hover:text-primary-600">
                        info@mwbtechs.com
                      </a>
                      <br />
                      <a href="mailto:sales@mwbtechs.com" className="text-gray-600 hover:text-primary-600">
                        sales@mwbtechs.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-secondary-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">{t('contact.phone')}</div>
                      <a href="tel:+1234567890" className="text-gray-600 hover:text-primary-600">
                        +1 (234) 567-890
                      </a>
                      <div className="text-sm text-gray-500 mt-1">{t('contact.phoneHours')}</div>
                    </div>
                  </div>

                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{t('contact.hours')}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('contact.monday')}</span>
                    <span className="font-semibold text-gray-900">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('contact.saturday')}</span>
                    <span className="font-semibold text-gray-900">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('contact.sunday')}</span>
                    <span className="font-semibold text-gray-900">{t('contact.closed')}</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Form Section */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2"
            >
              <div className="bg-white rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg">
                {/* Form Type Selector */}
                <div className="grid grid-cols-3 gap-2 mb-6 sm:mb-8">
                  {formTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        onClick={() => setActiveForm(type.id)}
                        className={`flex items-center justify-center gap-1.5 px-2 sm:px-4 py-2 rounded-lg transition-all text-sm sm:text-base ${
                          activeForm === type.id
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span className="text-xs sm:text-sm truncate">{type.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Success Message */}
                {submitted && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-green-900">{t('contact.success')}</div>
                      <div className="text-sm text-green-700">{t('contact.successDesc')}</div>
                    </div>
                  </motion.div>
                )}

                {/* Error Message */}
                {submitError && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
                  >
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-800">{submitError}</div>
                  </motion.div>
                )}

                {/* Forms */}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  {/* Common Fields */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.name')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        {...register('name', { required: t('contact.name') + ' ' + t('contact.required') })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="John Doe"
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.email')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        {...register('email', { required: t('contact.email') + ' ' + t('contact.required') })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="john@company.com"
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.company')}
                      </label>
                      <input
                        {...register('company')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={t('contact.companyPlaceholder')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.phone')}
                      </label>
                      <input
                        {...register('phone')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="+1 (234) 567-890"
                      />
                    </div>
                  </div>

                  {/* Consultation-specific fields */}
                  {activeForm === 'consultation' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.service')}
                      </label>
                      <select
                        {...register('service')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="">{t('contact.selectService')}</option>
                        <option value="digital-transformation">{t('contact.serviceDigitalTransformation')}</option>
                        <option value="process-automation">{t('contact.serviceProcessAutomation')}</option>
                        <option value="strategy">{t('contact.serviceStrategy')}</option>
                        <option value="change-management">{t('contact.serviceChangeManagement')}</option>
                      </select>
                    </div>
                  )}

                  {/* Quote-specific fields */}
                  {activeForm === 'quote' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('contact.projectType')}
                        </label>
                        <select
                          {...register('projectType')}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="">{t('contact.selectProjectType')}</option>
                          <option value="web-app">{t('contact.projectWebApp')}</option>
                          <option value="mobile-app">{t('contact.projectMobileApp')}</option>
                          <option value="api">{t('contact.projectApi')}</option>
                          <option value="modernization">{t('contact.projectModernization')}</option>
                          <option value="cloud">{t('contact.projectCloud')}</option>
                        </select>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('contact.budget')}
                          </label>
                          <select
                            {...register('budget')}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          >
                            <option value="">{t('contact.selectBudget')}</option>
                            <option value="5-10k">$5,000 - $10,000</option>
                            <option value="10-25k">$10,000 - $25,000</option>
                            <option value="25-50k">$25,000 - $50,000</option>
                            <option value="50k+">$50,000+</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('contact.timeline')}
                          </label>
                          <select
                            {...register('timeline')}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          >
                            <option value="">{t('contact.selectTimeline')}</option>
                            <option value="asap">{t('contact.timelineAsap')}</option>
                            <option value="1-3months">{t('contact.timeline1to3')}</option>
                            <option value="3-6months">{t('contact.timeline3to6')}</option>
                            <option value="6months+">{t('contact.timeline6plus')}</option>
                          </select>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.message')} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      {...register('message', { required: t('contact.message') + ' ' + t('contact.required') })}
                      rows={6}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder={
                        activeForm === 'quote'
                          ? t('contact.messagePlaceholderQuote')
                          : activeForm === 'consultation'
                          ? t('contact.messagePlaceholderConsultation')
                          : t('contact.messagePlaceholderGeneral')
                      }
                    />
                    {errors.message && (
                      <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full btn btn-primary text-lg disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? t('common.loading', 'Sending...') : t('contact.send')}
                    {!submitting && <Send className="w-5 h-5 ml-2" />}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
